import React, { useEffect, useState } from 'react';
import { cloneDeep } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DropdownButton, IDropdownIndividualButtonProps } from '@praktio/common-ui';
import { Container, Row, Col } from 'react-bootstrap';
import classNames from 'classnames';
import { Helmet } from 'react-helmet';

import { Table } from '../common/Table/Table';
import PageHeader from '../common/PageHeader/PageHeader';
import { RootState } from '../../ducks/store';
import { getUsers, clearUsersList } from '../../ducks/users';
import { Days, NotificationFrequency } from '../../utils/constants';
import RightPane from '../common/RightPane/RightPane';
import User from '../admin/User';
import UserCourseProgressDetails from './UserCourseProgressDetails/UserCourseProgressDetails';

import './Users.scss';

const UserHeaders = [
  { Header: 'Email', accessor: 'email', isId: true, className: 'Users-email' },
  { Header: 'First Name', accessor: 'firstName', className: 'Users-first' },
  { Header: 'Last Name', accessor: 'lastName', className: 'Users-last' },
  {
    Header: 'Notification',
    accessor: 'notification',
  },
];

const Users = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const userId = searchParams.get('userId');
  const courseId = searchParams.get('courseId');
  const { users } = useSelector((state: RootState) => {
    let users = undefined;
    if (state.users.list) {
      users = state.users.list.map((u) => {
        const user = cloneDeep(u);
        const { reportNotification } = user;
        let notification: string | number | undefined = reportNotification?.frequency || 'Not Set';
        if (reportNotification) {
          const { frequency, day, date } = reportNotification;
          switch (frequency) {
            case NotificationFrequency.Weekly:
              notification = Object.keys(Days).find((k) => Days[k] === day);
              break;
            case NotificationFrequency.Monthly:
              notification = date;
              break;
            default:
              break;
          }
        }

        return { ...user, notification };
      });
    }

    return { users };
  });

  const [rightPaneOpen, setRightPaneOpen] = useState(!!userId);

  useEffect(() => {
    dispatch(getUsers());
    return () => {
      dispatch(clearUsersList());
    };
  }, [dispatch]);

  useEffect(() => {
    setRightPaneOpen(!!userId);
  }, [userId]);

  const mainButton: IDropdownIndividualButtonProps = {
    text: 'Add User',
    action: () => {
      setSearchParams({ userId: 'new' });
    },
  };

  const subButtons: IDropdownIndividualButtonProps[] = [
    {
      text: 'Import Bulk Users',
      action: () => navigate(`/admin/users/bulk`),
    },
    {
      text: 'Edit Bulk Users',
      action: () => navigate(`/admin/users/bulk/edit`),
    },
  ];

  const user = userId ? users?.find((u) => u._id === userId) : undefined;
  return (
    <Container fluid className="Users">
      <Helmet>
        <title>{user ? `${user.firstName} ${user.lastName}` : 'Users'} - Praktio</title>
      </Helmet>
      <Row className="h-100">
        <Col
          className={classNames('p-0 h-100', {
            'd-none d-md-flex flex-column col-md-5 col-lg-7': rightPaneOpen,
            'd-flex flex-column col-xs-12': !rightPaneOpen,
          })}
        >
          <PageHeader pageName="Users">
            <DropdownButton mainButton={mainButton} subButtons={subButtons} />
          </PageHeader>
          <div className="Users-content">
            <Table
              columns={UserHeaders}
              data={users}
              activeRowId={userId || undefined}
              onRowClick={(row) => {
                setSearchParams({ userId: row.original._id });
              }}
            />
          </div>
        </Col>
        <RightPane open={rightPaneOpen}>
          {!!(userId && !courseId) && (
            <User
              additionalSaveAction={getUsers()}
              closeButtonAction={() => {
                setSearchParams('');
              }}
            />
          )}
          {!!(userId && courseId) && (
            <UserCourseProgressDetails
              closeButtonAction={() => {
                setSearchParams('');
              }}
            />
          )}
        </RightPane>
      </Row>
    </Container>
  );
};

export default Users;
